import React, { useState } from "react";
import { useSelector } from "react-redux";
import TimelinePicker from "./TimelinePicker";
import NotesInput from "./NotesInput";
import { taskConfig } from "./taskConfig";
import DropdownPill from "./DropDownPill";
import { useDispatch } from "react-redux";
import * as actions from "../../../../redux/actions/artistManager";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useArtistManagerActivityTracker } from "../../../../hooks/useArtistManagerActivityTracker";

const TaskPlanner = ({ initialData = {}, onSubmit, forAdd, onCancel }) => {
  const dispatch = useDispatch()
  const { trackTaskCompleted } = useArtistManagerActivityTracker();
  const [isLoading, setIsLoading] = useState(false);
  const [completed, setCompleted] = useState(initialData.completed || false);
  const [category, setCategory] = useState("");
  const [objective, setObjective] = useState(initialData.objective || "");
  const [kpi, setKpi] = useState(initialData.kpi || "");
  const [timeline, setTimeline] = useState(initialData.timeline || null);
  const [notes, setNotes] = useState(initialData.notes || "");
  const { hasActiveSubscription } = useSelector((state) => state.billing);

  // when initialData changes (editor reopened), update local state
  React.useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setCategory(initialData.category || "");
      setObjective(initialData.objective || "");
      setKpi(initialData.kpi || "");
      setTimeline(initialData.timeline || null);
      setNotes(initialData.notes || "");
      setCompleted(initialData.completed || false);
    }
  }, [initialData]);

  const selectedCategory = taskConfig[category] || null;

  const handleSubmit = (e) => {
    e.preventDefault()
    if (hasActiveSubscription) {
      const formatToMMDDYYYY = (d) => {
        if (!d) return null;
        let year, month, day;
        if (d instanceof Date) {
          if (isNaN(d.getTime())) return null;
          year = d.getFullYear();
          month = d.getMonth() + 1;
          day = d.getDate();
          return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
        }
        if (typeof d === 'string') {
          // Handle dd/mm/yyyy or mm/dd/yyyy
          if (d.includes('/')) {
            const parts = d.split('/').map((p) => p.trim());
            if (parts.length === 3) {
              const a = Number(parts[0]);
              const b = Number(parts[1]);
              const c = parts[2];
              // if first part > 12 treat as dd/mm/yyyy -> swap
              if (a > 12) {
                day = a; month = b; year = Number(c);
              } else {
                // ambiguous: assume incoming may already be mm/dd/yyyy; if year looks like 2-digit handle
                month = a; day = b; year = Number(c);
              }
              if (![day, month, year].some((n) => isNaN(n))) {
                return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
              }
            }
          }

          // Handle yyyy-mm-dd or dd-mm-yyyy
          if (d.includes('-')) {
            const parts = d.split('-').map((p) => p.trim());
            if (parts.length === 3) {
              const p0 = Number(parts[0]);
              const p1 = Number(parts[1]);
              const p2 = Number(parts[2]);
              if (p0 > 31) {
                // yyyy-mm-dd
                year = p0; month = p1; day = p2;
              } else {
                // dd-mm-yyyy
                day = p0; month = p1; year = p2;
              }
              if (![day, month, year].some((n) => isNaN(n))) {
                return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
              }
            }
          }

          // Fallback: try Date parse
          const parsed = new Date(d);
          if (!isNaN(parsed.getTime())) {
            year = parsed.getFullYear();
            month = parsed.getMonth() + 1;
            day = parsed.getDate();
            return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
          }
          return null;
        }
        return null;
      };

      const reqBody = {
        category: category,
        objective: objective,
        kpi: kpi,
        start_date: formatToMMDDYYYY(timeline?.startDate),
        end_date: formatToMMDDYYYY(timeline?.endDate),
        notes: notes,
        completed: false
      }
      dispatch(actions.createNewTask(toast, reqBody, setIsLoading));
      setCategory("");
      setObjective("");
      setKpi("");
      setTimeline(null);
      setNotes("");
    }
    if (forAdd) {
      onCancel();
    }
  };

  const handleUpdate = () => {
    const formatToMMDDYYYY = (d) => {
      if (!d) return null;
      let year, month, day;
      if (d instanceof Date) {
        if (isNaN(d.getTime())) return null;
        year = d.getFullYear();
        month = d.getMonth() + 1;
        day = d.getDate();
        return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
      }
      if (typeof d === 'string') {
        if (d.includes('/')) {
          const parts = d.split('/').map((p) => p.trim());
          if (parts.length === 3) {
            const a = Number(parts[0]);
            const b = Number(parts[1]);
            const c = parts[2];
            if (a > 12) {
              day = a; month = b; year = Number(c);
            } else {
              month = a; day = b; year = Number(c);
            }
            if (![day, month, year].some((n) => isNaN(n))) {
              return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
            }
          }
        }
        if (d.includes('-')) {
          const parts = d.split('-').map((p) => p.trim());
          if (parts.length === 3) {
            const p0 = Number(parts[0]);
            const p1 = Number(parts[1]);
            const p2 = Number(parts[2]);
            if (p0 > 31) {
              year = p0; month = p1; day = p2;
            } else {
              day = p0; month = p1; year = p2;
            }
            if (![day, month, year].some((n) => isNaN(n))) {
              return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
            }
          }
        }
        const parsed = new Date(d);
        if (!isNaN(parsed.getTime())) {
          year = parsed.getFullYear();
          month = parsed.getMonth() + 1;
          day = parsed.getDate();
          return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
        }
        return null;
      }
      return null;
    };

    const currentStart = formatToMMDDYYYY(timeline?.startDate);
    const currentEnd = formatToMMDDYYYY(timeline?.endDate);
    const prevStart = formatToMMDDYYYY(initialData?.timeline?.startDate);
    const prevEnd = formatToMMDDYYYY(initialData?.timeline?.endDate);

    const reqBody = {
      category: category,
      objective: objective,
      kpi: kpi,
      notes: notes,
      completed: completed
    };

    // Only include dates if they changed (including clearing them)
    if (currentStart !== prevStart) reqBody.start_date = currentStart;
    if (currentEnd !== prevEnd) reqBody.end_date = currentEnd;

    const shouldTrackCompletion = completed && !initialData?.completed;
    dispatch(actions.updateTask(
      toast,
      initialData?.id,
      reqBody,
      setIsLoading,
      shouldTrackCompletion ? () => trackTaskCompleted({ taskId: initialData?.id }) : undefined
    ));
    onCancel();
  }

  return (
    <>
      <ToastContainer />
      {(!initialData || Object.keys(initialData).length === 0) && (
        <>
          <div className="header-section flex flex-wrap items-center justify-between gap-2 text-white">
            <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight">
              <img src={require('../../../../assets/images/admin/Amplitude-Info.png')} alt="Amplitude Info" className='w-[30px] h-[30px]' /> Task Planner
            </div>
            <div className="flex items-center gap-2">
              <button disabled={category === '' || timeline === null} onClick={handleSubmit} type="button" className="btn-custom3 cursor-pointer">
                Add Task
                <i className="bi bi-plus-circle text-base ms-1"></i>
              </button>
              {forAdd && (
                <button onClick={onCancel} className="bg-white text-theme_clr1 font-bold px-3 py-2 rounded-lg">
                  ✕
                </button>
              )}
            </div>
          </div>
        </>
      )}
      <div className={`wrapper flex flex-col gap-5`}>
        {/* Category */}
        <DropdownPill
          label="📌 Category :"
          options={Object.keys(taskConfig)}
          value={category}
          onChange={(val) => {
            // when category changes, reset dependent fields
            setCategory(val);
            setObjective("");
            setKpi("");
          }}
        />

        {/* Objective */}
        <DropdownPill
          label="🎯 Objective :"
          options={selectedCategory?.objectives || []}
          value={objective}
          onChange={setObjective}
        />

        {/* KPI */}
        <DropdownPill
          label="📊 KPI (optional) :"
          options={selectedCategory?.kpis || []}
          value={kpi}
          onChange={setKpi}
        />

        {/* Timeline */}
        <TimelinePicker
          label="Timeline :"
          value={timeline}
          onChange={(e) => {
            setTimeline(e)
          }}
        />

        {/* Notes */}
        <NotesInput value={notes} onChange={setNotes} />
        {initialData && Object.keys(initialData).length > 0 && (
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="completedCheckbox"
                checked={completed}
                onChange={(e) => setCompleted(e.target.checked)}
                className="w-4 h-4 cursor-pointer accent-theme_clr1"
              />
              <label
                htmlFor="completedCheckbox"
                className="text-white text-base font-semibold cursor-pointer select-none"
              >
                {completed ? "Mark as Incomplete" : "Mark as Completed"}
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onCancel}
                className="text-theme_clr1 text-lg font-semibold px-3 py-1 rounded-lg bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="btn-custom3"
              >
                Update
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TaskPlanner;
